import type {
  ApiError,
  FeeConfigurationType,
  MemberDiscountLine,
  MemberListType,
  TermsConfig
} from '@types';

import { PaymentCollectionType } from '@features/group-management/constants';

export interface MemberTransactionHistoryItem {
  transaction_id: string;
  paid_amount: string;
  paid_at: string;
  settlement_status: string;
  payment_mode: string;
  payment_description: string;
  payment_method: string;
  utr: string;
  settled_at: string | null;
  settlement_at: string | null;
  settlement_type: string | null;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
  marked_paid_at: string | null;
  invoice_number: string;
  type?: string;
}

export interface Member {
  created_at: string;
  fee_amount?: string;
  group_member_id?: number;
  is_active: boolean;
  is_deletion_in_progress?: boolean;
  is_payment_link_sent: boolean;
  is_split_payment?: boolean;
  member_id?: string;
  member_image_url?: string;
  name: string;
  next_due_at: string;
  payment_due?: string;
  phone?: string;
  secondary_mobile: string;
  secondary_mobile_number?: string;
  amount?: string;
  primary_mobile?: string;
  primary_mobile_number?: string;
  fee_amount_configuration?: FeeConfigurationType[] | null;
  id: string;
  primary_country_code: string;
  secondary_country_code: string;
  email: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  pending_amount?: string;
  total_collected_amount?: string;
  total_amount?: string;
  group_name?: string;
  payment_schedule?: {
    current_term_amount?: string;
    current_term_number?: number;
    start_date?: string;
    end_date?: string;
    interval_frequency: number;
    interval: string;
    interval_execution_type?: string;
    marked_sessions_per_cycle?: number;
    sessions_per_cycle?: number;
    monthly_config?: {
      day_option?: string;
      custom_day?: number;
    };
    weekly_config?: {
      day_of_week?: string;
    };
    term_details?: TermsConfig[];
  };
  is_collection_started?: boolean;
  is_mandate_enabled?: boolean;
  mandate_max_amount?: string;
  is_attendance_based_fee_enabled?: boolean;
  group_member_attendance_data?: {
    present_count: number;
    total_attendance: number;
    percentage: string;
    sessions_attended: number;
    total_sessions_attended: number;
  };
  is_attendance_cycle_marked?: boolean;
  discounts?: MemberDiscountLine[];
  admission_number?: string;
  admission_date?: string;
  dob?: string;
  guardian_name?: string;
  age?: string;
  custom_fields?: Array<{ key: string; value: string }>;
}

export interface MemberAttendanceStatus {
  id: number;
  group_member_id: number;
  status: string;
  name: string;
  attendance_locked: boolean;
  remarks?: string;
}

export interface MemberAttendanceCount {
  present_count: number;
  absent_count: number;
  late_count: number;
  exempt_count: number;
}

export type UpdatemMemberApiResponse = {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  group_name: string;
  primary_mobile: string;
  secondary_mobile: string;
  amount: string;
  next_due_at: string;
  is_payment_link_sent: boolean;
  is_split_payment: boolean;
};

export interface EditMemberProps {
  isOpen: boolean;
  handleCancel: () => void;
  handleMemberUpdateSuccess: (member: MemberListType) => void;
  handleMemberUpdateError: (error: ApiError, member: MemberListType) => void;
  member: Member;
  paymentCollectionData: {
    type: PaymentCollectionType | '';
    intervalFrequency: number;
  };
  getApiParams: () => {
    organizationId: string;
    branchId: string;
    groupId: string;
  };
  isFeeConfigured?: boolean;
  feeConfigurationDetails?: FeeConfigurationType[];
  groupFrequency: string;
  groupIntervalFrequency: number;
  groupDayOption?: string;
  groupStartDate?: string;
  groupEndDate?: string;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  sessionsPerCycle?: number;
  defaultTermLabel?: string;
  groupName?: string;
  source?: 'group_member_list' | 'group_member_details_page';
}
export type AddMemberInput = {
  altNumber: string | null;
  amount: string | null;
  frequency?: string | null;
  monthsPerTerm?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  totalFee?: string | null;
  courseFee?: string | null;
  dueDate: string | null;
  name: string | null;
  primaryNumber: string | null;
  primaryNumberCode: string | null;
  altNumberCode: string | null;
  email: string | null;
  feeCategoryData?: {
    id: number | string;
    label: number | string;
    value: number | string;
  }[];
  amountPaid: string | null;
  admissionNumber: string | null;
  admissionDate: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  pincode: string | null;
  dateOfBirth: string | null;
  guardianName: string | null;
  age: string | null;
  bloodGroup?: BloodGroups | null;
  isInsured?: string | null;
  discountLabel?: string | null;
  discountAmount?: string | null;
};

export enum BloodGroups {
  A_POSITIVE = 'A+',
  A_NEGATIVE = 'A-',
  B_POSITIVE = 'B+',
  B_NEGATIVE = 'B-',
  AB_POSITIVE = 'AB+',
  AB_NEGATIVE = 'AB-',
  O_POSITIVE = 'O+',
  O_NEGATIVE = 'O-'
}

export interface MarkAsPaidBody {
  payment_method: string;
  payment_description: string;
  paid_date: string | null;
  terms: {
    term_number: number;
    amount: number;
  }[];
  settlement_account_id: number | null;
}
